import { Box, Grid, TextField } from '@mui/material';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { Button, Text } from '@bai/components';
import { logout } from '@bai/features/user';

import {
  handleLoginFeatureSubmit,
  LoginFeatureResponse,
  loginFeatureValidator,
  ValidationType,
} from './loginFeatureSubmitHandler';
import { ValidationMessageType } from './loginFeatureTypes';
import { TemporaryPassword } from './TemporaryPassword';

interface ForgotPasswordProps {
  primaryColor?: string;
  logo?: string;
  handleCancel: () => void;
  handleSubmit: (
    event: FormEvent,
    endpoint: string,
    password: string | null,
    username: string | null,
  ) => Promise<LoginFeatureResponse | string>;
  portalCode?: string;
  loginUsername?: string;
  hasCustomLogo: boolean;
  loginError?: string;
}

export function ForgotPassword({
  primaryColor,
  logo,
  handleCancel,
  handleSubmit,
  portalCode,
  loginUsername,
  hasCustomLogo,
  loginError,
}: ForgotPasswordProps) {
  const endpoint = 'api/account/forgotpassword';

  const navigate = useNavigate();
  const [showTemporaryPassword, setShowTemporaryPassword] = useState(false);
  const [username, setUserName] = useState(loginUsername || '');

  const [validationResponse, setValidationResponse] = useState({
    username: 'valid',
  });

  const [errorMsg, setErrorMsg] = useState(loginError);

  useEffect(() => {
    setErrorMsg(loginError);
  }, [loginError]);

  const handleCancelShowLogin = async () => {
    const loginView = '/';
    setUserName('');

    await logout(portalCode ?? '');
    void navigate(
      !portalCode ? loginView : `${loginView}?portal=${portalCode}`,
    );
  };

  let usernameValid =
    loginFeatureValidator(
      ValidationType.DEFAULTS_TO_EMPTY,
      null,
      username,
      null,
    ) && validationResponse.username === 'valid';

  const handleValidation = () => {
    usernameValid = loginFeatureValidator(
      ValidationType.IS_REQUIRED,
      null,
      username,
      null,
    );

    if (!usernameValid) {
      setValidationResponse({
        ...validationResponse,
        username: 'invalid',
      });
    } else {
      setValidationResponse({ ...validationResponse, username: 'valid' });
    }
  };

  const handleUsernameChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setUserName(e.target.value);
    setErrorMsg('');
    handleValidation();
  };

  const handleSubmitResponse = async (event: FormEvent) => {
    event.preventDefault();

    handleValidation();
    if (username === '') {
      return;
    }
    const forgotPassResp: LoginFeatureResponse | number = (await handleSubmit(
      event,
      endpoint,
      null,
      username,
    )) as LoginFeatureResponse | number;

    if (
      typeof forgotPassResp === 'object' &&
      forgotPassResp?.StatusCode === 200
    ) {
      setShowTemporaryPassword(true);
    } else if (typeof forgotPassResp === 'object') {
      setErrorMsg(forgotPassResp.ErrorMessage);
    }
  };

  const tempPasswordProps = {
    primaryColor,
    logo,
    handleCancel: handleCancelShowLogin,
    handleSubmit: handleLoginFeatureSubmit,
    username,
    portalCode,
    hasCustomLogo,
  };

  const forgotPasswordMessageLine1 =
    'Enter your username in the field below. Your password will be sent to the email address specified on your profile.';
  const forgotPasswordMessageLine2 =
    "Don't have an email? Contact your organization's Training Administrator.";
  const forgotPasswordMessageSx = {
    fontFamily: 'Calibri, sans-serif !important',
    fontSize: '1.2em',
    margin: '1em 0',
  };

  return (
    <Grid>
      <Box sx={{ backgroundColor: 'white', borderRadius: '25px' }}>
        {!showTemporaryPassword ? (
          <form
            onSubmit={(event: React.SyntheticEvent<HTMLFormElement>) =>
              handleSubmitResponse(event)
            }
          >
            <Grid sx={{ margin: '0 auto' }}>
              <Grid
                container
                sx={{
                  justifyContent: 'center',
                  margin: '0 auto',
                  width: hasCustomLogo ? '40%' : '60%',
                }}
              >
                <img
                  src={logo}
                  alt="logo"
                  className="themed_logo"
                  data-cy="logo"
                />
              </Grid>
              <h1>Forgot Your Password?</h1>

              <div style={{ width: '100%', margin: '2em 0' }}>
                <Text sx={forgotPasswordMessageSx}>
                  {forgotPasswordMessageLine1}
                </Text>

                <Text sx={forgotPasswordMessageSx}>
                  {forgotPasswordMessageLine2}
                </Text>
              </div>
              <TextField
                name="username"
                data-cy="fpusername"
                id="username"
                label="Username"
                variant="outlined"
                type="text"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleUsernameChange(event)
                }
                onBlur={handleValidation}
                onKeyUp={handleValidation}
                error={!usernameValid}
                helperText={
                  !usernameValid
                    ? `Username ${ValidationMessageType.IS_REQUIRED}`
                    : ''
                }
                style={{ width: '100%', marginBottom: '0.5em' }}
                value={username}
                slotProps={{
                  input: {
                    inputProps: {
                      role: 'textbox',
                      'aria-label': 'Username',
                    },
                  },
                }}
              />
              {errorMsg && (
                <Grid
                  className="alert alert-danger"
                  container
                  sx={{
                    alignItems: 'center',
                    fontFamily: 'Calibri !important',
                  }}
                >
                  <Grid
                    size={12}
                    sx={{ textAlign: 'left' }}
                    key="error-message"
                  >
                    {errorMsg}
                  </Grid>
                </Grid>
              )}

              <Button
                data-cy="forgot-password-button"
                type="submit"
                specificColorToUse={primaryColor}
              >
                Submit
              </Button>

              <Grid container>
                <Grid size={6} sx={{ padding: '1em 0 0 0' }}>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="as-link"
                    data-cy="cancel-forgot-password"
                  >
                    Cancel
                  </button>
                </Grid>
              </Grid>
            </Grid>
          </form>
        ) : (
          <TemporaryPassword {...tempPasswordProps} />
        )}
      </Box>
    </Grid>
  );
}
